import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Box, Drawer } from '@mui/material';
import type { DrawerProps } from '@mui/material';

// localStorage can throw (Safari private mode, sandboxed webviews, disabled
// storage). Persisting the panel width is best-effort — never let it break the UI.
function readStoredWidth(key: string): number | null {
  try {
    const raw = Number(localStorage.getItem(key));
    return Number.isFinite(raw) && raw > 0 ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredWidth(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // ignore — width simply won't persist
  }
}

interface ResizableDrawerProps extends Omit<DrawerProps, 'anchor'> {
  /** Minimum (and default) width in px. The panel can be dragged wider, never narrower. */
  minWidth: number;
  /** Max width as a fraction of the viewport (default 0.95). */
  maxViewportFraction?: number;
  /** localStorage key to persist the chosen width across sessions. */
  storageKey?: string;
  children: ReactNode;
}

/**
 * A right-anchored MUI Drawer with a drag handle pinned to its left edge so the
 * user can widen it. `minWidth` is the floor; the width is clamped to the
 * viewport and (optionally) persisted. On xs screens the panel stays full-width
 * and the handle is hidden.
 *
 * The handle is a fixed-position sibling (not a child of the scrolling paper),
 * so it never scrolls with the content and doesn't affect the paper's overflow.
 */
export default function ResizableDrawer({
  minWidth,
  maxViewportFraction = 0.95,
  storageKey,
  children,
  PaperProps,
  ...drawerProps
}: ResizableDrawerProps) {
  const clamp = useCallback(
    (w: number) => Math.min(Math.max(w, minWidth), window.innerWidth * maxViewportFraction),
    [minWidth, maxViewportFraction],
  );

  const [width, setWidth] = useState<number>(() => {
    const saved = storageKey ? readStoredWidth(storageKey) : null;
    const base = saved && saved >= minWidth ? saved : minWidth;
    return Math.min(base, window.innerWidth * maxViewportFraction);
  });
  const widthRef = useRef(width);
  widthRef.current = width;
  const draggingRef = useRef(false);

  const endDrag = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      setWidth(clamp(window.innerWidth - e.clientX));
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      endDrag();
      if (storageKey) writeStoredWidth(storageKey, Math.round(widthRef.current));
    };
    // Keep the panel on-screen if the viewport shrinks (e.g. a width saved on a
    // wider monitor, or a window resize).
    const onResize = () => setWidth((w) => clamp(w));
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('resize', onResize);
      // Never leave the whole app with a resize cursor / no-select if we unmount
      // mid-drag (route change, drawer closed while dragging).
      endDrag();
    };
  }, [clamp, endDrag, storageKey]);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  }, []);

  const { sx: paperSx, ...paperRest } = PaperProps ?? {};

  return (
    <>
      <Drawer
        anchor="right"
        {...drawerProps}
        PaperProps={{
          ...paperRest,
          sx: [
            { width: { xs: '100%', sm: width } },
            ...(paperSx == null ? [] : Array.isArray(paperSx) ? paperSx : [paperSx]),
          ],
        }}
      >
        {children}
      </Drawer>
      {drawerProps.open && (
        <Box
          onMouseDown={startResize}
          role="separator"
          aria-orientation="vertical"
          sx={{
            position: 'fixed',
            top: 0,
            bottom: 0,
            right: width,
            width: 8,
            ml: '-4px',
            cursor: 'col-resize',
            // Above the temporary Drawer's Modal (zIndex.modal) so the handle
            // sits on the panel's left edge and stays clickable.
            zIndex: (theme) => theme.zIndex.modal + 2,
            display: { xs: 'none', sm: 'block' },
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.35)' },
            '&:active': { bgcolor: 'rgba(139, 92, 246, 0.55)' },
          }}
        />
      )}
    </>
  );
}
